import { useEffect, useMemo, useState, useContext } from "react";
import axios from "axios";
import HeaderBar from "../../components/HeaderBar";
import PayRollLayoutWrapper from "../../components/PayRollLayoutWrapper";
import getCurrentSkin from "../../utils/getCurrentSkin";
import { ThemeContext } from "../../context/ThemeContext";

import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import { Printer, RefreshCcw } from "lucide-react";

const BASE_URL = "https://nissi-india.com";

const EmployeeOpeningLeave = () => {
  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;

  const finYearId = localStorage.getItem("finYearId") || "";

  // querystring: ?code=123&year=2017
  const qs = useMemo(() => new URLSearchParams(window.location.search), []);
  const empId = qs.get("code") || ""; // ASP used Request.QueryString[0]
  const year = qs.get("year") || "";

  // data
  const [rows, setRows] = useState([]);
  const [footerLapsedTotal, setFooterLapsedTotal] = useState(0);
  const [loading, setLoading] = useState(false);

  // pagination
  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  const screenBadge = useMemo(() => "INFRA SCREEN : C-51/2", []);

  const primaryBtnClass = `bg-${baseColor}-${baseShade}`;
  const primaryHoverClass = `hover:bg-${baseColor}-700`;

  const cardBase =
    `rounded-3xl border shadow-xl overflow-hidden ` +
    (isDark
      ? "border-white/10 bg-gradient-to-br from-white/15 to-white/5 backdrop-blur-md"
      : "border-gray-200 bg-white");

  const primaryBtn =
    `inline-flex items-center justify-center gap-2 px-6 py-3 rounded-2xl text-white font-bold shadow-lg ` +
    `hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${primaryBtnClass} ${primaryHoverClass}`;

  const ghostBtn =
    `inline-flex items-center justify-center gap-2 px-6 py-3 rounded-2xl font-bold border transition-all duration-200 ` +
    (isDark
      ? "border-white/10 text-white hover:bg-white/10"
      : "border-gray-200 text-gray-800 hover:bg-gray-50");

  const fetchList = async (p = page) => {
    if (!String(finYearId).trim()) {
      toast.warn("Please select Financial Year.");
      return;
    }
    if (!String(empId).trim() || !String(year).trim()) {
      toast.error("Missing query params: code (empId) and year");
      return;
    }

    try {
      setLoading(true);
      const res = await axios.get(`${BASE_URL}/api/employee-opening-leave/data`, {
        params: {
          finYearId: Number(finYearId),
          code: Number(empId),
          year: Number(year),
          page: p,
          pageSize,
        },
      });

      if (res?.data?.success) {
        setRows(res.data.rows || []);
        setTotal(res.data.total || 0);
        setTotalPages(res.data.totalPages || 1);
        setPage(res.data.page || p);
        setFooterLapsedTotal(res.data.footerLapsedTotal || 0);
      } else {
        toast.error(res?.data?.message || "Failed to load data");
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchList(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchList(page);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  const handlePrint = () => {
    if (!String(empId).trim() || !String(year).trim()) {
      toast.warn("Missing empId/year for print.");
      return;
    }
    const qs2 = new URLSearchParams({
      code: String(empId),
      year: String(year),
    }).toString();

    // ✅ open print page in new tab
    window.open(`/PayRoll/EmployeeOpeningLeavePrint?${qs2}`, "_blank", "noopener,noreferrer");
  };

  const showingFrom = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const showingTo = Math.min(page * pageSize, total);

  return (
    <PayRollLayoutWrapper>
      <ToastContainer position="top-right" autoClose={2600} theme={isDark ? "dark" : "light"} />

      <div className={`min-h-screen p-6 ${isDark ? "bg-gray-900" : "bg-gray-100"}`}>
        <HeaderBar
          title={<span className="font-dmserif text-3xl">EMPLOYEE OPENING LEAVE</span>}
          setSkin={setSkin}
          currentSkin={skin}
        />

        <div
          className={`mx-auto max-w-7xl p-6 mt-6 rounded-3xl shadow-2xl border ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
              : `${currentSkin.glass} bg-white text-black border-gray-200`
          }`}
        >
          {/* Top row */}
          <div className="flex flex-col xl:flex-row xl:items-center xl:justify-between gap-4 mb-6">
            <div className="flex flex-wrap items-center gap-4">
              <div className={`text-2xl font-extrabold ${isDark ? "text-white" : "text-gray-900"}`}>
                Employee Opening Leave
              </div>

              <div className={`px-4 py-2 rounded-2xl text-sm font-bold ${isDark ? "bg-blue-900/40 text-blue-100" : "bg-blue-100 text-blue-800"}`}>
                {screenBadge}
              </div>

              <div className={`px-4 py-2 rounded-2xl text-sm font-bold ${isDark ? "bg-emerald-900/40 text-emerald-100" : "bg-emerald-100 text-emerald-800"}`}>
                {total} Records
              </div>

              <div className={`px-4 py-2 rounded-2xl text-sm font-bold ${isDark ? "bg-purple-900/40 text-purple-100" : "bg-purple-100 text-purple-800"}`}>
                EmpID: {empId} | Year: {year}
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-3 justify-end">
              <button onClick={() => fetchList(page)} className={ghostBtn} disabled={loading}>
                <RefreshCcw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
                Refresh
              </button>

              <button onClick={handlePrint} className={primaryBtn} disabled={loading}>
                <Printer className="w-4 h-4" />
                Print
              </button>
            </div>
          </div>

          {/* Table */}
          <div className={cardBase}>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className={`${primaryBtnClass} text-white`}>
                  <tr>
                    <th className="px-3 py-3 text-center font-bold">S.No</th>
                    <th className="px-3 py-3 text-center font-bold">EmployeeCode</th>
                    <th className="px-3 py-3 text-center font-bold">EmployeeName</th>
                    <th className="px-3 py-3 text-center font-bold">Year</th>
                    <th className="px-3 py-3 text-center font-bold">Month</th>
                    <th className="px-3 py-3 text-center font-bold">TotalDays</th>
                    <th className="px-3 py-3 text-center font-bold">Workingdays</th>
                    <th className="px-3 py-3 text-center font-bold">TotalLeave</th>
                    <th className="px-3 py-3 text-center font-bold">MonthlyCasualleave</th>
                    <th className="px-3 py-3 text-center font-bold">Balanceleave</th>
                    <th className="px-3 py-3 text-center font-bold">OpenningLeave</th>
                    <th className="px-3 py-3 text-center font-bold">SavingLeave</th>
                    <th className="px-3 py-3 text-center font-bold">BalanceLeave/LOP</th>
                    <th className="px-3 py-3 text-center font-bold">Lapsed Leave</th>
                  </tr>
                </thead>

                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {loading ? (
                    Array.from({ length: pageSize }).map((_, idx) => (
                      <tr key={idx} className={isDark ? "bg-gray-900" : "bg-white"}>
                        {Array.from({ length: 14 }).map((__, c) => (
                          <td key={c} className="px-3 py-3">
                            <div className="h-4 bg-gray-300 dark:bg-gray-600 rounded animate-pulse" />
                          </td>
                        ))}
                      </tr>
                    ))
                  ) : rows.length === 0 ? (
                    <tr>
                      <td colSpan={14} className={`px-4 py-12 text-center ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                        No Records Found
                      </td>
                    </tr>
                  ) : (
                    rows.map((r, idx) => (
                      <tr key={idx} className={`${isDark ? "bg-gray-900" : "bg-white"}`}>
                        <td className={`px-3 py-3 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {(page - 1) * pageSize + idx + 1}
                        </td>
                        <td className={`px-3 py-3 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {r.fld_Employeecode}
                        </td>
                        <td className={`px-3 py-3 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {r.fld_Employee}
                        </td>
                        <td className={`px-3 py-3 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {r.year}
                        </td>
                        <td className={`px-3 py-3 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {r.Month}
                        </td>
                        <td className={`px-3 py-3 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {r.TotalDays}
                        </td>
                        <td className={`px-3 py-3 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {r.Workingdays}
                        </td>
                        <td className={`px-3 py-3 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {r.TotalLeave}
                        </td>
                        <td className={`px-3 py-3 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {r.MonthlyCasualleave}
                        </td>
                        <td className={`px-3 py-3 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {r.Balanceleave ?? r.BalanceLeave}
                        </td>
                        <td className={`px-3 py-3 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {r.Openningleave ?? r.OpenningLeave}
                        </td>
                        <td className={`px-3 py-3 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {r.SavingLeave}
                        </td>
                        <td className={`px-3 py-3 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {r.TotBalanceLeave}
                        </td>
                        <td className={`px-3 py-3 text-sm text-center font-bold ${isDark ? "text-gray-200" : "text-gray-900"}`}>
                          {r.LapsedLeave}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>

                {/* ✅ ASP footer */}
                <tfoot>
                  <tr className={isDark ? "bg-gray-800" : "bg-gray-100"}>
                    <td colSpan={13} className="px-3 py-3 text-center font-extrabold">
                      Total
                    </td>
                    <td className="px-3 py-3 text-center font-extrabold">
                      {footerLapsedTotal}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>

          {/* Pagination */}
          <div className="flex flex-col md:flex-row items-center justify-between mt-6 gap-4">
            <div className={`text-sm ${isDark ? "text-gray-300" : "text-gray-600"}`}>
              Showing <b>{showingFrom}</b> to <b>{showingTo}</b> of <b>{total}</b>
            </div>

            {totalPages > 1 && (
              <div className="inline-flex rounded-2xl overflow-hidden border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900">
                <button
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className={`px-4 py-2 text-sm font-bold ${
                    page === 1
                      ? "text-gray-400 cursor-not-allowed"
                      : "hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-800 dark:text-gray-200"
                  }`}
                >
                  Prev
                </button>

                {[...Array(totalPages)].map((_, i) => (
                  <button
                    key={i}
                    onClick={() => setPage(i + 1)}
                    className={`px-4 py-2 text-sm font-bold transition ${
                      page === i + 1
                        ? `${primaryBtnClass} text-white`
                        : "hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-800 dark:text-gray-200"
                    }`}
                  >
                    {i + 1}
                  </button>
                ))}

                <button
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                  className={`px-4 py-2 text-sm font-bold ${
                    page === totalPages
                      ? "text-gray-400 cursor-not-allowed"
                      : "hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-800 dark:text-gray-200"
                  }`}
                >
                  Next
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </PayRollLayoutWrapper>
  );
};

export default EmployeeOpeningLeave;
